import type React from 'react';
import { calculatePlan } from '../../api/calculate';
import { buildApiPayload } from '../../utils/formPayload';
import { updatePetPlan } from '../../services/customerAccount';
import { clearUpdateMode } from '../../hooks/useUpdateMode';
import { clearDraft } from '../../utils/localStorageDraft';
import type { AppConfigResponse } from '../../types/config';
import type { FormDataSubset } from './checkoutTypes';

interface UpdateSubmitResult {
  degraded: boolean;
}

interface UpdateSubmitParams {
  subscriptionId: string;
  petId: string;
  formData: FormDataSubset;
  normalizedSelectedRecipeIds: string[];
  config: AppConfigResponse;
  mountedRef: React.MutableRefObject<boolean>;
  onError: (msg: string) => void;
}

export async function runUpdateSubmit({
  subscriptionId,
  petId,
  formData,
  normalizedSelectedRecipeIds,
  config,
  mountedRef,
  onError,
}: UpdateSubmitParams): Promise<UpdateSubmitResult | null> {
  try {
    // El backend es la única fuente de verdad para el cálculo nutricional.
    // No existe fallback a la aproximación local: si el backend falla, el
    // guardado se bloquea y onError informa al usuario.
    const plan = await calculatePlan(
      // FormDataSubset usa `string` para gender, pero buildApiPayload acepta
      // Partial<DogFormData>. El cast es seguro: los valores reales son los
      // mismos literales que el enum Gender.
      buildApiPayload(formData as Parameters<typeof buildApiPayload>[0], {
        selectedRecipes: normalizedSelectedRecipeIds,
      }),
    );

    const monthlyPlanDistribution: Record<string, number> = {};
    for (const r of plan.recipes) {
      if (r.monthly_cans > 0) {
        monthlyPlanDistribution[r.external_variant_id] = r.monthly_cans;
      }
    }

    // Mi Cuenta / Appstle sigue hablando en variant id (contrato /account/* diferido
    // a #36). El funnel ya trabaja en UUID canónico, así que traducimos aquí los ids
    // seleccionados (recetas y suplementos) al variant de Shopify que /account/* espera.
    const variantByProductId: Record<string, string> = {};
    for (const p of config.products || []) {
      if (p.id && p.external_variant_id) variantByProductId[p.id] = p.external_variant_id;
    }
    const supplements = config.supplement_rules?.supplements || [];
    const selectedRecipeVariantIds = normalizedSelectedRecipeIds
      .map((id) => variantByProductId[id] || id)
      .filter(Boolean);
    const supplementVariantIds = (formData.selectedSupplementVariantIds || [])
      .map((id) => {
        const sup = supplements.find((s) => s.id === id || s.external_variant_id === id);
        return sup?.external_variant_id || id;
      })
      .filter(Boolean);

    const result = await updatePetPlan(subscriptionId, petId, {
      name: formData.nombre || undefined,
      breedKey: formData.raza || undefined,
      breedDisplayName:
        (formData.raza ? config.breed_database?.breeds?.[formData.raza]?.display_name : undefined) || undefined,
      weightKg: formData.peso || undefined,
      ageMonths: formData.ageTotalMonths || undefined,
      ageCategory: formData.ageCategory || undefined,
      bodyCondition: formData.bodyCondition || undefined,
      activityLevel: formData.activityLevel || undefined,
      gender: formData.gender || undefined,
      isNeutered: formData.isNeutered ?? undefined,
      pathologies: formData.pathologies || [],
      allergies: formData.proteinAllergies || [],
      dietType: formData.dietType || undefined,
      dailyKcal: Math.round(plan.der),
      selectedRecipeVariantIds: selectedRecipeVariantIds.length > 0 ? selectedRecipeVariantIds : undefined,
      selectedSupplementVariantIds: supplementVariantIds.length > 0 ? supplementVariantIds : undefined,
      monthlyPlanDistribution: Object.keys(monthlyPlanDistribution).length > 0 ? monthlyPlanDistribution : undefined,
    });

    // Limpiar SIEMPRE (aunque el componente se haya desmontado): el update en
    // Appstle ya se completó y dejar el draft vivo ofrecería "Continuar plan"
    // de un plan ya actualizado.
    clearUpdateMode();
    clearDraft();
    if (!mountedRef.current) return null;
    return { degraded: result.subscription_updated === false };
  } catch (err) {
    onError((err as Error).message || 'No pudimos actualizar el plan.');
    return null;
  }
}
